import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Phone, ChevronDown, CalendarClock } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/constants/site";
import { NAV } from "@/constants/testIds";

function MegaItem({ item, activeId, setActiveId }) {
  const isOpen = activeId === item.id;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  return (
    <div
      className="relative"
      onMouseEnter={() => setActiveId(item.id)}
      onMouseLeave={() => setActiveId(null)}
    >
      <NavLink
        to={item.to}
        end={item.to === "/"}
        data-testid={NAV.link(item.id)}
        className={({ isActive }) =>
          `inline-flex items-center gap-1.5 text-[15px] font-semibold transition-colors py-2 ${
            isActive
              ? "text-[var(--text)]"
              : "text-[var(--text-muted)] hover:text-[var(--text)]"
          }`
        }
      >
        {item.label}
        {hasChildren && (
          <ChevronDown
            size={13}
            className={`transition-transform ${isOpen ? "rotate-180 text-[var(--accent)]" : ""}`}
          />
        )}
      </NavLink>

      {hasChildren && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-full left-1/2 -translate-x-1/2 mt-3 z-30"
              data-testid={`mega-${item.id}`}
            >
              <div className="w-[520px] max-w-[92vw] bg-white border border-[var(--border)] rounded-2xl shadow-[0_24px_60px_-20px_rgba(11,18,32,0.25)] p-3">
                <div className="grid grid-cols-1 gap-1">
                  {item.children.map((c) => (
                    <Link
                      key={c.to + c.label}
                      to={c.to}
                      data-testid={`mega-${item.id}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`group flex items-start gap-4 p-3 rounded-xl hover:bg-[var(--surface)] transition-colors ${
                        c.nested ? "pl-9 relative" : ""
                      }`}
                      onClick={() => setActiveId(null)}
                    >
                      {c.nested && (
                        <span
                          aria-hidden
                          className="absolute left-4 top-0 bottom-1/2 border-l border-b border-cyan-300/60 rounded-bl-md w-4"
                        />
                      )}
                      <span
                        className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 group-hover:scale-125 transition-transform ${
                          c.nested
                            ? "bg-cyan-400/80"
                            : "bg-gradient-to-r from-cyan-400 to-sky-500"
                        }`}
                      />
                      <span className="min-w-0">
                        <span
                          className={`block font-editorial leading-tight text-[var(--text)] group-hover:text-[var(--accent)] transition-colors ${
                            c.nested ? "text-[14px]" : "text-[15px]"
                          }`}
                        >
                          {c.label}
                        </span>
                        {c.body && (
                          <span className="mt-1 block text-[12px] text-[var(--text-muted)] leading-snug">
                            {c.body}
                          </span>
                        )}
                      </span>
                      <ArrowRight
                        size={14}
                        className="ml-auto text-[var(--text-dim)] group-hover:text-[var(--accent)] group-hover:translate-x-0.5 transition-all"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const [expandedMobile, setExpandedMobile] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveMega(null);
    setExpandedMobile(null);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-4 left-4 right-4 md:left-6 md:right-6 z-50 transition-all duration-500 rounded-2xl ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(11,18,32,0.15)] border border-[var(--border)]"
            : "bg-white/70 backdrop-blur-md border border-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-5 md:px-6 flex items-center justify-between h-[92px]">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="Magee Enterprises"
              className="h-14 md:h-14 transition-opacity duration-300 group-hover:opacity-80"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((item) => (
              <MegaItem
                key={item.id}
                item={item}
                activeId={activeMega}
                setActiveId={setActiveMega}
              />
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+18882992250"
              data-testid="nav-phone"
              className="group flex items-center gap-3 pl-1 pr-4 py-1 rounded-full hover:bg-[var(--surface)] transition-colors"
            >
              <span className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(6,182,212,0.5)]">
                <Phone size={16} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">
                  Service Team
                </span>
                <span className="font-editorial text-[15px] font-extrabold tracking-tight text-[var(--text)] group-hover:text-[var(--accent)] transition-colors">
                  888-299-2250
                </span>
              </span>
            </a>
            <Link
              to="/contact#book"
              data-testid="nav-book-call"
              className="hidden lg:inline-flex items-center gap-2 text-[13px] font-medium text-[var(--text)] border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors rounded-full py-2.5 px-4"
            >
              <CalendarClock size={14} />
              Book a 15-min call
            </Link>
            <Link
              to="/contact"
              data-testid={NAV.cta}
              className="btn-primary group text-[13px] py-2.5 px-5"
            >
              Request Quote
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* Mobile controls */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href="tel:+18882992250"
              data-testid="nav-phone-mobile"
              aria-label="Call service team 888-299-2250"
              className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(6,182,212,0.5)]"
            >
              <Phone size={16} />
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid={NAV.mobileToggle}
              className="text-[var(--text)] p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MEGA MENU */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-4 top-[100px] z-40 bg-white border border-[var(--border)] rounded-2xl shadow-xl md:hidden max-h-[80vh] overflow-y-auto"
          >
            <div className="flex flex-col p-5 gap-1">
              {NAV_LINKS.map((item, i) => {
                const has = item.children && item.children.length > 0;
                const isEx = expandedMobile === item.id;
                return (
                  <div key={item.id} className="border-b border-[var(--border)] last:border-b-0">
                    <div className="flex items-center justify-between py-3">
                      <Link
                        to={item.to}
                        data-testid={NAV.link(`${item.id}-mobile`)}
                        className="font-editorial text-2xl text-[var(--text)] flex items-baseline gap-3"
                      >
                        <span className="font-mono-brand text-[10px] text-[var(--text-dim)]">
                          0{i + 1}
                        </span>
                        {item.label}
                      </Link>
                      {has && (
                        <button
                          onClick={() => setExpandedMobile(isEx ? null : item.id)}
                          aria-label="Toggle submenu"
                          className="w-9 h-9 rounded-full border border-[var(--border)] flex items-center justify-center"
                        >
                          <ChevronDown
                            size={14}
                            className={`transition-transform ${isEx ? "rotate-180 text-[var(--accent)]" : ""}`}
                          />
                        </button>
                      )}
                    </div>
                    <AnimatePresence>
                      {has && isEx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-6 pb-3 flex flex-col gap-1">
                            {item.children.map((c) => (
                              <Link
                                key={c.to + c.label}
                                to={c.to}
                                className={`group py-2 flex items-start gap-3 ${
                                  c.nested ? "pl-6 relative" : ""
                                }`}
                              >
                                {c.nested && (
                                  <span
                                    aria-hidden
                                    className="absolute left-1 top-0 bottom-1/2 border-l border-b border-cyan-300/60 rounded-bl-md w-3"
                                  />
                                )}
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-sky-500 shrink-0" />
                                <span>
                                  <span className="block font-editorial text-[16px] leading-tight">
                                    {c.label}
                                  </span>
                                  {c.body && (
                                    <span className="text-[12px] text-[var(--text-muted)]">
                                      {c.body}
                                    </span>
                                  )}
                                </span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              <Link to="/contact" className="btn-primary mt-3 justify-center">
                Request Quote <ArrowRight size={14} />
              </Link>
              <Link
                to="/contact#book"
                data-testid="nav-book-call-mobile"
                className="mt-2 inline-flex items-center justify-center gap-2 border border-[var(--border)] rounded-full py-3 text-[13px] font-medium text-[var(--text)]"
              >
                <CalendarClock size={14} /> Book a 15-min call
              </Link>
              <a
                href="tel:+18882992250"
                className="mt-2 flex items-center gap-3 rounded-2xl border border-[var(--border)] p-3"
              >
                <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                  <Phone size={18} />
                </span>
                <span className="flex flex-col">
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    Service Team
                  </span>
                  <span className="font-editorial text-lg font-extrabold text-[var(--text)]">
                    888-299-2250
                  </span>
                </span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
